import {ArrowsPointingOutIcon, MagnifyingGlassMinusIcon, MagnifyingGlassPlusIcon} from "@heroicons/react/20/solid";
import type {WellAllocation} from "@ots-frontend/ots-data";
import {
  Background,
  Edge,
  EdgeProps,
  Handle,
  MiniMap,
  Node,
  NodeProps,
  Panel,
  Position,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  useViewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {sankey, SankeyGraph, SankeyLink, SankeyNode, sankeyLinkHorizontal} from "d3-sankey";
import {useMemo} from "react";

export type AllocationSankeyProps = {
  allocations: WellAllocation[];
  sourceLabel?: string;
  zoomable?: boolean;
  defaultZoom?: number;
  viewportHeight?: number;
};

type AllocationLayoutNode = {
  id: string;
  kind: "source" | "well";
  order: number;
  allocation?: WellAllocation;
};

type AllocationLayoutLink = {
  id: string;
  order: number;
  allocation: WellAllocation;
};

type AllocationNodeData = {
  kind: "source" | "well";
  label: string;
  allocation?: WellAllocation;
};

type AllocationEdgeData = {
  path: string;
  width: number;
  allocation: WellAllocation;
};

type AllocationFlowNode = Node<AllocationNodeData, "allocationSource" | "allocationWell">;
type AllocationFlowEdge = Edge<AllocationEdgeData, "allocationLink">;

type AllocationFlowLayout = {
  nodes: AllocationFlowNode[];
  edges: AllocationFlowEdge[];
};

const SOURCE_ID = "allocation-source";
const SOURCE_X = 196;
const TARGET_X = 736;
const NODE_WIDTH = 16;
const WELL_NODE_WIDTH = 390;
const WELL_NODE_HEIGHT = 52;
const LAYOUT_TOP = 34;
const percent = new Intl.NumberFormat(undefined, {style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 2});
const number = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});

const isLayoutNode = (
  value: number | string | SankeyNode<AllocationLayoutNode, AllocationLayoutLink>,
): value is SankeyNode<AllocationLayoutNode, AllocationLayoutLink> => typeof value === "object";

const buildLayout = (allocations: WellAllocation[], sourceLabel: string): AllocationFlowLayout => {
  const height = Math.max(300, allocations.length * 66 + LAYOUT_TOP * 2);
  const hasPositiveAllocation = allocations.some((allocation) => allocation.allocation > 0);
  const graph: SankeyGraph<AllocationLayoutNode, AllocationLayoutLink> = {
    nodes: [
      {id: SOURCE_ID, kind: "source", order: -1},
      ...allocations.map((allocation, order) => ({
        id: `well-${allocation.wellId}`,
        kind: "well" as const,
        order,
        allocation,
      })),
    ],
    links: allocations.map((allocation, order) => ({
      id: `allocation-${allocation.wellId}`,
      order,
      allocation,
      source: SOURCE_ID,
      target: `well-${allocation.wellId}`,
      // D3 needs a positive total to position an all-zero result. This fallback is used for
      // layout only; zero allocations remain zero-width dashed links in the rendered chart.
      value: hasPositiveAllocation ? Math.max(0, allocation.allocation) : 1,
    })),
  };

  const layout = sankey<AllocationLayoutNode, AllocationLayoutLink>()
    .nodeId((node) => node.id)
    .nodeWidth(NODE_WIDTH)
    .nodePadding(58)
    .nodeSort((left, right) => left.order - right.order)
    .linkSort((left, right) => left.order - right.order)
    .iterations(12)
    .extent([[SOURCE_X, LAYOUT_TOP], [TARGET_X + NODE_WIDTH, height - LAYOUT_TOP]])(graph);
  const pathForLink = sankeyLinkHorizontal<AllocationLayoutNode, AllocationLayoutLink>();
  const source = layout.nodes.find((node) => node.id === SOURCE_ID);
  const sourceY = Number.isFinite(source?.y0) ? source?.y0 ?? LAYOUT_TOP : LAYOUT_TOP;
  const sourceHeight = Number.isFinite(source?.y1)
    ? Math.max(72, (source?.y1 ?? height - LAYOUT_TOP) - sourceY)
    : Math.max(72, height - LAYOUT_TOP * 2);

  const nodes: AllocationFlowNode[] = [{
    id: SOURCE_ID,
    type: "allocationSource",
    position: {x: SOURCE_X - 176, y: sourceY},
    data: {kind: "source", label: sourceLabel},
    style: {width: 176 + NODE_WIDTH, height: sourceHeight},
    draggable: false,
    selectable: false,
  }];

  layout.nodes.forEach((node) => {
    if (node.kind !== "well" || !node.allocation) return;
    const incoming = node.targetLinks?.[0];
    const centerY = Number.isFinite(incoming?.y1)
      ? incoming?.y1 ?? LAYOUT_TOP
      : LAYOUT_TOP + node.order * 66 + 33;
    nodes.push({
      id: node.id,
      type: "allocationWell",
      position: {x: Number.isFinite(node.x0) ? node.x0 ?? TARGET_X : TARGET_X, y: centerY - WELL_NODE_HEIGHT / 2},
      data: {kind: "well", label: node.allocation.wellname, allocation: node.allocation},
      style: {width: WELL_NODE_WIDTH, height: WELL_NODE_HEIGHT},
      draggable: false,
      selectable: true,
    });
  });

  const edges = layout.links.flatMap<AllocationFlowEdge>((link) => {
    if (!isLayoutNode(link.source) || !isLayoutNode(link.target)) return [];
    const path = pathForLink(link as SankeyLink<AllocationLayoutNode, AllocationLayoutLink>);
    if (!path) return [];
    return [{
      id: link.id,
      source: link.source.id,
      target: link.target.id,
      type: "allocationLink",
      data: {
        path,
        width: link.allocation.allocation > 0 ? Math.max(2, link.width ?? 0) : 0,
        allocation: link.allocation,
      },
      selectable: false,
      focusable: true,
      zIndex: link.allocation.allocation > 0 ? 1 : 0,
    }];
  });

  return {nodes, edges};
};

const AllocationSourceNode = ({data}: NodeProps<AllocationFlowNode>) => (
  <div className="pointer-events-none relative flex h-full w-full items-start justify-end">
    <div className="absolute left-0 top-0 max-w-40 text-left">
      <div className="text-sm font-bold leading-tight text-[var(--ots-text)]">{data.label}</div>
      <div className="mt-1 text-xs font-semibold tabular-nums text-[var(--ots-text-muted)]">100%</div>
    </div>
    <div className="h-full w-4 rounded-full bg-[var(--ots-primary)] shadow-sm"/>
    <Handle type="source" position={Position.Right} className="!size-px !border-0 !bg-transparent !opacity-0"/>
  </div>
);

const AllocationWellNode = ({data, selected}: NodeProps<AllocationFlowNode>) => {
  const allocation = data.allocation;
  if (!allocation) return null;
  const detail = allocation.allocation === 0
    ? "Eligible, no allocated production"
    : `${number.format(allocation.adjustedVolume)} adjusted volume · ${number.format(allocation.average)} average`;
  return (
    <div
      title={`${allocation.wellname}: ${percent.format(allocation.allocation)}. ${detail}`}
      className={[
        "flex h-full w-full items-center gap-3 rounded-lg px-0.5 transition-colors",
        selected ? "bg-[var(--ots-primary-container)]/35" : "bg-transparent",
      ].join(" ")}>
      <Handle type="target" position={Position.Left} className="!size-px !border-0 !bg-transparent !opacity-0"/>
      <span
        aria-hidden="true"
        className={[
          "h-8 w-3 shrink-0 rounded",
          allocation.allocation > 0
            ? "bg-[var(--ots-primary)] shadow-sm"
            : "border border-[var(--ots-outline)] bg-[var(--ots-surface-variant)]",
        ].join(" ")}/>
      <span className="min-w-0">
        <span className="block truncate text-sm font-bold leading-tight text-[var(--ots-text)]">{allocation.wellname}</span>
        <span className="mt-1 block truncate text-xs text-[var(--ots-text-muted)]">
          <strong className="font-semibold tabular-nums text-[var(--ots-text)]">{percent.format(allocation.allocation)}</strong>
          {allocation.allocation === 0 ? " · eligible, no allocated production" : " · final allocation"}
        </span>
      </span>
    </div>
  );
};

const AllocationLink = ({data}: EdgeProps<AllocationFlowEdge>) => {
  if (!data) return null;
  const isZero = data.allocation.allocation === 0;
  return (
    <g className="react-flow__edge-path">
      <path
        d={data.path}
        fill="none"
        stroke={isZero ? "var(--ots-outline)" : "var(--ots-primary)"}
        strokeDasharray={isZero ? "5 5" : undefined}
        strokeLinecap={isZero ? "round" : "butt"}
        strokeOpacity={isZero ? 0.8 : 0.42}
        strokeWidth={isZero ? 1.5 : data.width}
        className="transition-[stroke-opacity] hover:stroke-opacity-70">
        <title>{`${data.allocation.wellname}: ${percent.format(data.allocation.allocation)} allocation`}</title>
      </path>
      <path d={data.path} fill="none" stroke="transparent" strokeWidth={Math.max(14, data.width)}>
        <title>{`${data.allocation.wellname}: ${percent.format(data.allocation.allocation)} allocation`}</title>
      </path>
    </g>
  );
};

const nodeTypes = {
  allocationSource: AllocationSourceNode,
  allocationWell: AllocationWellNode,
};
const edgeTypes = {allocationLink: AllocationLink};

const AllocationViewportControls = ({defaultZoom}: {defaultZoom: number}) => {
  const {zoom} = useViewport();
  const {fitView, zoomIn, zoomOut} = useReactFlow();
  const reset = () => void fitView({duration: 180, padding: 0.12, maxZoom: defaultZoom});
  return (
    <Panel position="top-right" className="!m-2">
      <div className="flex items-center gap-1 rounded-lg border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]/95 p-1 shadow-sm backdrop-blur">
        <span className="hidden px-1 text-[10px] font-medium text-[var(--ots-text-muted)] sm:inline">Drag to pan</span>
        <button type="button" aria-label="Zoom allocation chart out" onClick={() => void zoomOut({duration: 120})} className="rounded-md p-1.5 text-[var(--ots-text-muted)] hover:bg-[var(--ots-surface-variant)] hover:text-[var(--ots-text)]">
          <MagnifyingGlassMinusIcon className="size-4"/>
        </button>
        <button type="button" aria-label={`${Math.round(zoom * 100)}%`} onClick={reset} title="Fit allocation chart" className="min-w-12 rounded-md px-1.5 py-1 text-[11px] font-semibold tabular-nums text-[var(--ots-text)] hover:bg-[var(--ots-surface-variant)]">
          {Math.round(zoom * 100)}%
        </button>
        <button type="button" aria-label="Zoom allocation chart in" onClick={() => void zoomIn({duration: 120})} className="rounded-md p-1.5 text-[var(--ots-text-muted)] hover:bg-[var(--ots-surface-variant)] hover:text-[var(--ots-text)]">
          <MagnifyingGlassPlusIcon className="size-4"/>
        </button>
        <button type="button" aria-label="Fit allocation chart" onClick={reset} className="rounded-md p-1.5 text-[var(--ots-text-muted)] hover:bg-[var(--ots-surface-variant)] hover:text-[var(--ots-text)]">
          <ArrowsPointingOutIcon className="size-4"/>
        </button>
      </div>
    </Panel>
  );
};

const AllocationSankeyCanvas = ({allocations, sourceLabel, zoomable, defaultZoom, viewportHeight}: Required<AllocationSankeyProps>) => {
  const flow = useMemo(() => buildLayout(allocations, sourceLabel), [allocations, sourceLabel]);
  return (
    <div
      role="region"
      aria-label={`${sourceLabel} split across ${allocations.length} eligible wells`}
      className="overflow-hidden rounded-xl border border-[var(--ots-outline-variant)] bg-[var(--ots-surface)]"
      style={{height: viewportHeight}}>
      <ReactFlow<AllocationFlowNode, AllocationFlowEdge>
        nodes={flow.nodes}
        edges={flow.edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        minZoom={0.25}
        maxZoom={2.5}
        fitView
        fitViewOptions={{padding: 0.12, maxZoom: defaultZoom}}
        nodesDraggable={false}
        nodesConnectable={false}
        edgesReconnectable={false}
        elementsSelectable
        panOnDrag
        zoomOnDoubleClick={zoomable}
        zoomOnPinch={zoomable}
        zoomOnScroll={zoomable}
        preventScrolling={zoomable}
        proOptions={{hideAttribution: true}}>
        <Background color="var(--ots-outline-variant)" gap={28} size={1}/>
        {zoomable && <AllocationViewportControls defaultZoom={defaultZoom}/>}
        {allocations.length >= 10 && (
          <MiniMap
            ariaLabel="Allocation chart overview"
            pannable
            zoomable={zoomable}
            position="bottom-right"
            nodeColor={(node) => node.type === "allocationSource" ? "#3148ad" : "#64748b"}
            maskColor="rgba(100, 116, 139, 0.12)"
            className="!m-2 !h-20 !w-32 !rounded-lg !border !border-[var(--ots-outline-variant)] !bg-[var(--ots-surface)]"/>
        )}
      </ReactFlow>
    </div>
  );
};

/** Proportional well-allocation Sankey with a pannable, zoomable React Flow viewport. */
export const AllocationSankey = ({
  allocations,
  sourceLabel = "Allocatable ticket cost",
  zoomable = true,
  defaultZoom = 1,
  viewportHeight = 520,
}: AllocationSankeyProps) => (
  <ReactFlowProvider>
    <AllocationSankeyCanvas
      allocations={allocations}
      sourceLabel={sourceLabel}
      zoomable={zoomable}
      defaultZoom={Math.min(2.5, Math.max(0.25, defaultZoom))}
      viewportHeight={viewportHeight}/>
  </ReactFlowProvider>
);
